import axios from 'axios';
import {
  API_BASE_ELECTRONIC_PRESCRIPTION_URL,
  API_ELECTRONIC_PRESCRIPTION_GET_ALL,
  API_ELECTRONIC_PRESCRIPTION_CREATE,
  API_ELECTRONIC_PRESCRIPTION_GET_BY_ID,
  API_ELECTRONIC_PRESCRIPTION_UPDATE,
  API_ELECTRONIC_PRESCRIPTION_SIGN,
  API_ELECTRONIC_PRESCRIPTION_EMIT,
  API_ELECTRONIC_PRESCRIPTION_CANCEL,
  API_ELECTRONIC_PRESCRIPTION_BY_DOCTOR,
  API_ELECTRONIC_PRESCRIPTION_BY_PATIENT,
  API_ELECTRONIC_PRESCRIPTION_DISPENSATION,
  API_ELECTRONIC_PRESCRIPTION_EXPIRING,
  API_ELECTRONIC_PRESCRIPTION_HISTORY,
  API_ELECTRONIC_PRESCRIPTION_SEARCH,
  API_ELECTRONIC_PRESCRIPTION_STATS_DOCTOR
} from '../../../helpers/urlHelper';

/**
 * 💊 SERVICIO DE RECETAS ELECTRÓNICAS
 * Maneja todas las operaciones CRUD y funcionalidades específicas de recetas electrónicas
 */
class ElectronicPrescriptionService {
  
  // ========================================================================
  // 🔧 CONSTRUCCIÓN DE URLs
  // ========================================================================
  
  /**
   * Construir URL completa con parámetros
   * @param {string} endpoint - Endpoint específico
   * @param {string|null} id - ID para reemplazar :id
   * @param {Object} params - Query parameters
   * @returns {string} URL completa
   */
  buildUrl(endpoint, id = null, params = {}) {
    let url = `${API_BASE_ELECTRONIC_PRESCRIPTION_URL}${endpoint}`;
    
    // Reemplazar :id si se proporciona
    if (id && endpoint.includes(':id')) {
      url = url.replace(':id', id);
    }
    
    // Agregar query parameters
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    
    const queryString = queryParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  }

  /**
   * URLs específicas
   */
  getCreateUrl() {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_CREATE);
  }

  getByIdUrl(id) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_GET_BY_ID, id);
  }

  getUpdateUrl(id) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_UPDATE, id);
  }

  getSignUrl(id) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_SIGN, id);
  }

  getEmitUrl(id) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_EMIT, id);
  }

  getCancelUrl(id) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_CANCEL, id);
  }

  getByDoctorUrl(doctorId, params = {}) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_BY_DOCTOR, doctorId, params);
  }

  getByPatientUrl(patientId, params = {}) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_BY_PATIENT, patientId, params);
  }

  getDispensationUrl(id) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_DISPENSATION, id);
  }

  getExpiringUrl(params = {}) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_EXPIRING, null, params);
  }

  getHistoryUrl(id) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_HISTORY, id);
  }

  // En la sección de URLs específicas
getAllUrl(params = {}) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_GET_ALL, null, params);
}

getSearchUrl(params = {}) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_SEARCH, null, params);
}

getStatsUrl(doctorId, params = {}) {
    return this.buildUrl(API_ELECTRONIC_PRESCRIPTION_STATS_DOCTOR, doctorId, params);
}

  // ========================================================================
  // 📝 OPERACIONES BÁSICAS CRUD
  // ========================================================================
  
  /**
   * Crear nueva receta electrónica
   * @param {Object} prescriptionData - Datos de la receta
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async createElectronicPrescription(prescriptionData) {
    try {
      const response = await axios.post(
        this.getCreateUrl(),
        this.formatPrescriptionData(prescriptionData)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al crear la receta electrónica');
    }
  }

  /**
   * Obtener receta por ID
   * @param {string} id - ID de la receta
   * @returns {Promise<Object>} Datos de la receta
   */
  async getElectronicPrescriptionById(id) {
    try {
      const response = await axios.get(this.getByIdUrl(id));
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener la receta electrónica');
    }
  }

  /**
   * Actualizar receta electrónica
   * @param {string} id - ID de la receta
   * @param {Object} updateData - Datos a actualizar
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async updateElectronicPrescription(id, updateData) {
    try {
      const response = await axios.put(
        this.getUpdateUrl(id),
        this.formatPrescriptionData(updateData)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al actualizar la receta electrónica');
    }
  }

  // ========================================================================
  // ✍️ OPERACIONES DE FIRMA Y EMISIÓN
  // ========================================================================

  /**
   * Firmar receta electrónica
   * @param {string} id - ID de la receta
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async signElectronicPrescription(id) {
    try {
      const response = await axios.post(this.getSignUrl(id));
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al firmar la receta electrónica');
    }
  }

  /**
   * Emitir receta electrónica
   * @param {string} id - ID de la receta
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async emitElectronicPrescription(id) {
    try {
      const response = await axios.post(this.getEmitUrl(id));
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al emitir la receta electrónica');
    }
  }

  /**
   * Cancelar receta electrónica
   * @param {string} id - ID de la receta
   * @param {Object} cancellationData - Datos de cancelación (reason, detailedReason)
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async cancelElectronicPrescription(id, cancellationData) {
    try {
      const response = await axios.delete(
        this.getCancelUrl(id),
        { data: cancellationData }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al cancelar la receta electrónica');
    }
  }

  // ========================================================================
  // 🔍 CONSULTAS Y BÚSQUEDAS
  // ========================================================================

  /**
   * Obtener recetas por médico
   * @param {string} doctorId - ID del médico
   * @param {Object} filters - Filtros de búsqueda
   * @returns {Promise<Object>} Lista de recetas
   */
  async getElectronicPrescriptionsByDoctor(doctorId, filters = {}) {
    try {
      const response = await axios.get(this.getByDoctorUrl(doctorId, filters));
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener las recetas del médico');
    }
  }

  /**
   * Obtener recetas por paciente
   * @param {string} patientId - ID del paciente
   * @param {Object} filters - Filtros de búsqueda
   * @returns {Promise<Object>} Lista de recetas
   */
  async getElectronicPrescriptionsByPatient(patientId, filters = {}) {
    try {
      const response = await axios.get(this.getByPatientUrl(patientId, filters));
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener las recetas del paciente');
    }
  }

  // ========================================================================
  // 🏥 OPERACIONES DE FARMACIA
  // ========================================================================

  /**
   * Registrar dispensación
   * @param {string} id - ID de la receta
   * @param {Object} dispensationData - Datos de dispensación
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async registerDispensation(id, dispensationData) {
    try {
      const response = await axios.post(
        this.getDispensationUrl(id),
        dispensationData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al registrar la dispensación');
    }
  }

  // ========================================================================
  // 🔧 UTILIDADES
  // ========================================================================

  /**
   * Obtener recetas que expiran pronto
   * @param {number} days - Número de días para considerar "pronto a expirar"
   * @returns {Promise<Object>} Lista de recetas próximas a expirar
   */
  async getExpiringPrescriptions(days = 2) {
    try {
      const response = await axios.get(this.getExpiringUrl({ days }));
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener las recetas próximas a expirar');
    }
  }

  /**
   * Obtener historial de una receta
   * @param {string} id - ID de la receta
   * @returns {Promise<Object>} Historial de la receta
   */
  async getPrescriptionHistory(id) {
    try {
      const response = await axios.get(this.getHistoryUrl(id));
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener el historial de la receta');
    }
  }

  // ========================================================================
  // 🔧 MÉTODOS DE UTILIDAD Y VALIDACIÓN
  // ========================================================================

 /**
 * Validar datos de receta antes de enviar
 * @param {Object} prescriptionData - Datos de la receta a validar
 * @returns {Object} Resultado de la validación
 */
validatePrescriptionData(prescriptionData) {
    const errors = [];

    // Validaciones básicas del paciente
    if (!prescriptionData.patientInfo?.userId) {
        errors.push('ID de paciente es requerido');
    }

    if (!prescriptionData.patientInfo?.fullName?.trim()) {
        errors.push('Nombre completo del paciente es requerido');
    }

    if (!prescriptionData.patientInfo?.idNumber?.trim()) {
        errors.push('Número de identificación es requerido');
    }

    if (!prescriptionData.patientInfo?.dateOfBirth) {
        errors.push('Fecha de nacimiento es requerida');
    }

    // ✅ CORRECCIÓN DEFINITIVA: Buscar medication directamente
    const medicationInfo = prescriptionData.medication;
    
    if (!medicationInfo) {
        errors.push('Información del medicamento es requerida');
    } else {
        if (!medicationInfo.codigoNacional) {
            errors.push('Código nacional del medicamento es requerido');
        }

        if (!medicationInfo.nombre?.trim()) {
            errors.push('Nombre del medicamento es requerido');
        }

        if (!medicationInfo.principioActivo?.trim()) {
            errors.push('Principio activo es requerido');
        }

        if (!medicationInfo.formaFarmaceutica?.trim()) {
            errors.push('Forma farmacéutica es requerida');
        }

        if (!medicationInfo.dosisUnidad?.trim()) {
            errors.push('Dosis unidad es requerida');
        }

        if (!medicationInfo.viaAdministracion?.trim()) {
            errors.push('Vía de administración es requerida');
        }

        if (!medicationInfo.laboratorio?.trim()) {
            errors.push('Laboratorio es requerido');
        }

        if (medicationInfo.unidadesPorEnvase === undefined || medicationInfo.unidadesPorEnvase < 1) {
            errors.push('Unidades por envase debe ser mayor a 0');
        }
    }

    // Validaciones de posología
    if (!prescriptionData.posology?.cantidad) {
        errors.push('Cantidad en posología es requerida');
    }

    if (!prescriptionData.posology?.frecuencia?.trim()) {
        errors.push('Frecuencia en posología es requerida');
    }

    if (!prescriptionData.posology?.unidad) {
        errors.push('Unidad en posología es requerida');
    }

    // Validaciones de tratamiento
    if (!prescriptionData.treatmentDuration?.value || prescriptionData.treatmentDuration.value < 1) {
        errors.push('Duración del tratamiento debe ser mayor a 0');
    }

    if (!prescriptionData.numberOfPackages || prescriptionData.numberOfPackages < 1) {
        errors.push('Número de envases debe ser mayor a 0');
    }

    // Validaciones del centro médico
    if (!prescriptionData.medicalCenter?.name?.trim()) {
        errors.push('Nombre del centro médico es requerido');
    }

    if (!prescriptionData.medicalCenter?.address?.trim()) {
        errors.push('Dirección del centro médico es requerida');
    }

    if (!prescriptionData.medicalCenter?.city?.trim()) {
        errors.push('Ciudad del centro médico es requerida');
    }

    // Validaciones del prescriptor
    if (!prescriptionData.prescriberInfo?.userId) {
        errors.push('ID del prescriptor es requerido');
    }

    if (!prescriptionData.prescriberInfo?.fullName?.trim()) {
        errors.push('Nombre completo del prescriptor es requerido');
    }

    if (!prescriptionData.prescriberInfo?.collegiateNumber?.trim()) {
        errors.push('Número de colegiación es requerido');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

  /**
 * Formatear datos de receta para envío al servidor
 * @param {Object} rawData - Datos sin formato
 * @returns {Object} Datos formateados
 */
formatPrescriptionData(rawData) {
  return {
    patientInfo: {
      userId: rawData.patientInfo?.userId,
      fullName: rawData.patientInfo?.fullName?.trim(),
      idType: rawData.patientInfo?.idType || 'DNI',
      idNumber: rawData.patientInfo?.idNumber?.trim(),
      dateOfBirth: rawData.patientInfo?.dateOfBirth
    },
    // ✅ FIX: Add the missing prescriberInfo field
    prescriberInfo: {
      userId: rawData.prescriberInfo?.userId,
      fullName: rawData.prescriberInfo?.fullName?.trim(),
      collegiateNumber: rawData.prescriberInfo?.collegiateNumber?.trim(),
      specialty: rawData.prescriberInfo?.specialty || 'Medicina General',
      professionalAddress: rawData.prescriberInfo?.professionalAddress,
      city: rawData.prescriberInfo?.city,
      zipCode: rawData.prescriberInfo?.zipCode,
      phone: rawData.prescriberInfo?.phone,
      email: rawData.prescriberInfo?.email
    },
    medication: {
      codigoNacional: rawData.medication?.codigoNacional?.toString(),
      nombre: rawData.medication?.nombre?.trim(),
      principioActivo: rawData.medication?.principioActivo?.trim(),
      formaFarmaceutica: rawData.medication?.formaFarmaceutica,
      dosisUnidad: rawData.medication?.dosisUnidad,
      viaAdministracion: rawData.medication?.viaAdministracion,
      unidadesPorEnvase: rawData.medication?.unidadesPorEnvase,
      laboratorio: rawData.medication?.laboratorio,
      financiado: rawData.medication?.financiado,
      requiereReceta: rawData.medication?.requiereReceta,
      generico: rawData.medication?.generico,
      precio: rawData.medication?.precio
    },
    posology: {
      cantidad: rawData.posology?.cantidad,
      unidad: rawData.posology?.unidad || 'comprimido',
      frecuencia: rawData.posology?.frecuencia?.trim()
    },
    treatmentDuration: {
      value: parseInt(rawData.treatmentDuration?.value) || 1,
      unit: rawData.treatmentDuration?.unit || 'DIAS'
    },
    numberOfPackages: parseInt(rawData.numberOfPackages) || 1,
    prescriptionType: rawData.prescriptionType || 'AGUDA',
    specialInstructions: rawData.specialInstructions?.trim() || '',
    automaticRenewal: rawData.automaticRenewal || { enabled: false, maxRenewals: 0 },
    gastricProtector: rawData.gastricProtector || { added: false, automatic: false },
    medicalCenter: {
      name: rawData.medicalCenter?.name?.trim(),
      address: rawData.medicalCenter?.address?.trim(),
      city: rawData.medicalCenter?.city?.trim(),
      zipCode: rawData.medicalCenter?.zipCode,
      phone: rawData.medicalCenter?.phone,
      cif: rawData.medicalCenter?.cif,
      email: rawData.medicalCenter?.email
    },
    metadata: rawData.metadata || {}
  };
}

  /**
   * Validar datos de cancelación
   * @param {Object} cancellationData - Datos de cancelación
   * @returns {Object} Resultado de la validación
   */
  validateCancellationData(cancellationData) {
    const errors = [];
    const validReasons = ['ERROR_MEDICO', 'SOLICITUD_PACIENTE', 'EFECTO_ADVERSO', 'INTERACCION_GRAVE', 'OTRO'];

    if (!cancellationData.reason) {
      errors.push('Razón de cancelación es requerida');
    } else if (!validReasons.includes(cancellationData.reason)) {
      errors.push('Razón de cancelación no válida');
    }

    if (cancellationData.detailedReason && cancellationData.detailedReason.length > 500) {
      errors.push('La razón detallada no puede exceder los 500 caracteres');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validar datos de dispensación
   * @param {Object} dispensationData - Datos de dispensación
   * @returns {Object} Resultado de la validación
   */
  validateDispensationData(dispensationData) {
    const errors = [];

    if (!dispensationData.pharmacyId?.trim()) {
      errors.push('ID de farmacia es requerido');
    }

    if (!dispensationData.pharmacyName?.trim()) {
      errors.push('Nombre de farmacia es requerido');
    }

    if (!dispensationData.pharmacyCif?.trim()) {
      errors.push('CIF de farmacia es requerido');
    } else {
      const cifRegex = /^[A-Z]\d{8}$/;
      if (!cifRegex.test(dispensationData.pharmacyCif)) {
        errors.push('CIF debe tener formato: una letra seguida de 8 dígitos');
      }
    }

    if (!dispensationData.quantity || dispensationData.quantity < 1) {
      errors.push('La cantidad debe ser mayor a 0');
    }

    if (dispensationData.dispensationNotes && dispensationData.dispensationNotes.length > 500) {
      errors.push('Las notas no pueden exceder los 500 caracteres');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Obtener estado legible de una receta
   * @param {string} status - Estado de la receta
   * @returns {string} Estado en español legible
   */
  getReadableStatus(status) {
    const statusMap = {
      'BORRADOR': 'Borrador',
      'FIRMADA': 'Firmada',
      'EMITIDA': 'Emitida',
      'DISPENSADA_PARCIAL': 'Dispensada Parcial',
      'DISPENSADA_TOTAL': 'Dispensada Total',
      'CADUCADA': 'Caducada',
      'ANULADA': 'Anulada'
    };

    return statusMap[status] || status;
  }

  /**
   * Obtener color para el estado
   * @param {string} status - Estado de la receta
   * @returns {string} Clase CSS para el color
   */
  getStatusColor(status) {
    const colorMap = {
      'BORRADOR': 'text-gray-600',
      'FIRMADA': 'text-blue-600',
      'EMITIDA': 'text-green-600',
      'DISPENSADA_PARCIAL': 'text-orange-600',
      'DISPENSADA_TOTAL': 'text-green-800',
      'CADUCADA': 'text-red-600',
      'ANULADA': 'text-red-800'
    };

    return colorMap[status] || 'text-gray-500';
  }

  /**
   * Obtener badge CSS completo para el estado
   * @param {string} status - Estado de la receta
   * @returns {string} Clases CSS para el badge
   */
  getStatusBadge(status) {
    const badgeMap = {
      'BORRADOR': 'bg-gray-100 text-gray-800',
      'FIRMADA': 'bg-blue-100 text-blue-800',
      'EMITIDA': 'bg-green-100 text-green-800',
      'DISPENSADA_PARCIAL': 'bg-orange-100 text-orange-800',
      'DISPENSADA_TOTAL': 'bg-green-200 text-green-900',
      'CADUCADA': 'bg-red-100 text-red-800',
      'ANULADA': 'bg-red-200 text-red-900'
    };

    return badgeMap[status] || 'bg-gray-100 text-gray-800';
  }

  /**
   * Formatear fecha para mostrar
   * @param {string|Date} date - Fecha a formatear
   * @returns {string} Fecha formateada
   */
  formatDate(date) {
    if (!date) return '-';
    
    try {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '-';
    }
  }

  /**
   * Calcular días hasta expiración
   * @param {string|Date} expirationDate - Fecha de expiración
   * @returns {number} Días hasta expiración (negativo si ya expiró)
   */
  getDaysUntilExpiration(expirationDate) {
    if (!expirationDate) return null;
    
    try {
      const now = new Date();
      const expiry = new Date(expirationDate);
      const diffTime = expiry - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      return null;
    }
  }

  // ========================================================================
// 🔍 MÉTODOS DE CONSULTA ADICIONALES
// ========================================================================

/**
 * Obtener TODAS las recetas electrónicas con filtros y paginación
 * @param {Object} filters - Filtros de búsqueda y paginación
 * @returns {Promise<Object>} Lista paginada de todas las recetas
 */
async getAllElectronicPrescriptions(filters = {}) {
    try {
        const response = await axios.get(this.getAllUrl(filters));
        return response.data;
    } catch (error) {
        throw this.handleError(error, 'Error al obtener todas las recetas electrónicas');
    }
}

/**
 * Buscar recetas electrónicas
 * @param {Object} searchParams - Parámetros de búsqueda
 * @returns {Promise<Object>} Resultados de búsqueda
 */
async searchElectronicPrescriptions(searchParams = {}) {
    try {
        const response = await axios.get(this.getSearchUrl(searchParams));
        return response.data;
    } catch (error) {
        throw this.handleError(error, 'Error al buscar recetas electrónicas');
    }
}

/**
 * Obtener estadísticas de un médico
 * @param {string} doctorId - ID del médico
 * @param {Object} params - Parámetros adicionales (fechas, etc.)
 * @returns {Promise<Object>} Estadísticas del médico
 */
async getDoctorStats(doctorId, params = {}) {
    try {
        // Primero intentar obtener estadísticas del endpoint específico
        const response = await axios.get(this.getStatsUrl(doctorId, params));
        return response.data;
    } catch (error) {
        // Si falla, calcular estadísticas manualmente
        console.warn('Endpoint de estadísticas no disponible, calculando manualmente:', error);
        return await this.calculateDoctorStatsManually(doctorId, params);
    }
}

/**
 * Calcular estadísticas del médico manualmente
 * @param {string} doctorId - ID del médico
 * @param {Object} params - Parámetros adicionales
 * @returns {Promise<Object>} Estadísticas calculadas
 */
async calculateDoctorStatsManually(doctorId, params = {}) {
    try {
        // Obtener todas las recetas del médico
        const prescriptionsResponse = await this.getElectronicPrescriptionsByDoctor(doctorId, {
            limit: 1000, // Obtener un número alto para estadísticas
            ...params
        });

        const prescriptions = prescriptionsResponse.data?.electronicPrescriptions || [];

        // Calcular estadísticas básicas
        const totalPrescriptions = prescriptions.length;
        
        // Contar pacientes únicos
        const uniquePatients = new Set();
        const medicationCounts = {};

        prescriptions.forEach(prescription => {
            // Agregar paciente único
            if (prescription.patientInfo?.userId?._id) {
    uniquePatients.add(prescription.patientInfo.userId._id);
    console.log('Paciente agregado al servicio:', prescription.patientInfo.userId._id, '-', prescription.patientInfo.fullName);
}
// Fallback por si userId es un string directo
else if (prescription.patientInfo?.userId && typeof prescription.patientInfo.userId === 'string') {
    uniquePatients.add(prescription.patientInfo.userId);
    console.log('Paciente agregado (string):', prescription.patientInfo.userId);
}

            // Contar medicamentos
            const medicationName = prescription.medication?.nombre || 'Medicamento no especificado';
            medicationCounts[medicationName] = (medicationCounts[medicationName] || 0) + 1;
        });

        // Convertir conteos de medicamentos a array ordenado
        const topMedications = Object.entries(medicationCounts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10); // Top 10

        // Calcular estadísticas por estado
        const statusCounts = {
            BORRADOR: 0,
            FIRMADA: 0,
            EMITIDA: 0,
            DISPENSADA_PARCIAL: 0,
            DISPENSADA_TOTAL: 0,
            CADUCADA: 0,
            ANULADA: 0
        };

        prescriptions.forEach(prescription => {
            const status = prescription.status || 'BORRADOR';
            if (statusCounts.hasOwnProperty(status)) {
                statusCounts[status]++;
            }
        });

        // Calcular tendencias por mes (últimos 6 meses)
        const monthlyTrends = this.calculateMonthlyTrends(prescriptions);

        return {
            data: {
                totalPrescriptions,
                totalPatients: uniquePatients.size,
                topMedications,
                statusDistribution: statusCounts,
                monthlyTrends,
                averagePrescriptionsPerPatient: uniquePatients.size > 0 
                    ? Math.round((totalPrescriptions / uniquePatients.size) * 100) / 100 
                    : 0,
                lastUpdated: new Date().toISOString()
            },
            success: true
        };

    } catch (error) {
        console.error('Error calculando estadísticas manualmente:', error);
        
        // Retornar estadísticas mínimas por defecto
        return {
            data: {
                totalPrescriptions: 0,
                totalPatients: 0,
                topMedications: [],
                statusDistribution: {
                    BORRADOR: 0,
                    FIRMADA: 0,
                    EMITIDA: 0,
                    DISPENSADA_PARCIAL: 0,
                    DISPENSADA_TOTAL: 0,
                    CADUCADA: 0,
                    ANULADA: 0
                },
                monthlyTrends: [],
                averagePrescriptionsPerPatient: 0,
                lastUpdated: new Date().toISOString()
            },
            success: true
        };
    }
}

/**
 * Calcular tendencias mensuales
 * @param {Array} prescriptions - Array de prescripciones
 * @returns {Array} Datos de tendencias mensuales
 */
calculateMonthlyTrends(prescriptions) {
    const trends = {};
    const now = new Date();
    
    // Inicializar últimos 6 meses
    for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        trends[key] = {
            month: date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long' }),
            count: 0
        };
    }

    // Contar prescripciones por mes
    prescriptions.forEach(prescription => {
        if (prescription.createdAt) {
            const date = new Date(prescription.createdAt);
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            if (trends[key]) {
                trends[key].count++;
            }
        }
    });

    return Object.values(trends);
}
// ========================================================================
// 💡 MÉTODOS DE CONVENIENCIA
// ========================================================================

/**
 * Obtener recetas activas de un paciente
 * @param {string} patientId - ID del paciente
 * @returns {Promise<Object>} Recetas activas del paciente
 */
async getActivePatientPrescriptions(patientId) {
    return this.getElectronicPrescriptionsByPatient(patientId, { active: true });
}

/**
 * Obtener recetas recientes (últimos 30 días)
 * @param {Object} additionalFilters - Filtros adicionales
 * @returns {Promise<Object>} Recetas recientes
 */
async getRecentPrescriptions(additionalFilters = {}) {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return this.getAllElectronicPrescriptions({
        dateFrom: thirtyDaysAgo.toISOString(),
        ...additionalFilters
    });
}

/**
 * Obtener recetas por estado específico
 * @param {string} status - Estado de las recetas
 * @param {Object} additionalFilters - Filtros adicionales
 * @returns {Promise<Object>} Recetas con el estado especificado
 */
async getPrescriptionsByStatus(status, additionalFilters = {}) {
    return this.getAllElectronicPrescriptions({
        status,
        ...additionalFilters
    });
}

  // ========================================================================
  // 🔧 MANEJO DE ERRORES
  // ========================================================================

  /**
   * Maneja errores de las peticiones HTTP
   * @param {Error} error - Error capturado
   * @param {string} defaultMessage - Mensaje por defecto
   * @returns {Error} Error procesado
   */
  handleError(error, defaultMessage) {
    console.log('=== ANÁLISIS COMPLETO DEL ERROR ===');
    console.log('error objeto completo:', error);
    console.log('error.response:', error.response?.data);
    console.log('error.request:', error.request);
    console.log('==================================');
    
    if (error.response) {
        // Error del servidor con respuesta
        const message = error.response.data?.message || defaultMessage;
        const status = error.response.status;
        
        console.error(`Error ${status}:`, message);
        
        return {
            message,
            status,
            errors: error.response.data?.errors || [],
            validationErrors: error.response.data?.validationErrors || [],
            response: error.response.data // Añadir toda la respuesta para debugging
        };
    } else if (error.request) {
        // Error de red
        console.error('Error de red:', error.message);
        return {
            message: 'Error de conexión. Verifique su conexión a internet.',
            status: 0,
            errors: []
        };
    } else {
        // Error de configuración
        console.error('Error:', error.message);
        return {
            message: defaultMessage,
            status: 500,
            errors: []
        };
    }
}
}



// Crear y exportar instancia única del servicio
const electronicPrescriptionService = new ElectronicPrescriptionService();
export default electronicPrescriptionService;